import * as React from 'react';
import { PaymentTypes } from '@grandvisionhq/api-client';
import { Context } from '@grandvisionhq/carts-v2';
import { MediaQuery, Loader, Logo, Alert, SupportFooter } from '@grandvisionhq/common';
import { SplitPane } from '@grandvisionhq/layout';
import { useIntl, useFeatureToggles, useSelector } from '@grandvisionhq/state';
import { pushPurchaseEventToDataLayer, trackOrderCompletionEvents } from '../data-layer.js';
import { isFailed } from '../selectors.js';
import { getInvoice, getSelectedStore, getServiceStore, getSelectedPickupPoint } from '../StepShipping/selectors.js';
import { OrderConfirm } from './OrderConfirm.js';
import { getSelectedPaymentDetails } from './utils/getSelectedPaymentDetails.js';
import { useGetOrderData } from './hooks/use-get-order-data.js';
import { useSummariesSlot } from '../slots/summaries/summary-context.js';

const OrderConfirmContainer = ({ orderData , shippingMethodsAsides , status , cartId , cart , logo  })=>{
    var ref, ref1, ref2, ref3, ref4;
    const { getLabel , currency  } = useIntl();
    const [orderResponse, isLoading] = useGetOrderData(orderData);
    const { isMonetateProductDataEnabled  } = useFeatureToggles();
    const labels = React.useMemo(()=>({
            title: getLabel('checkout.completed.title'),
            callbackResult: {
                authorised: getLabel('checkout.authorised.message', {
                    hideLabelKey: true
                }),
                cancelled: getLabel('checkout.cancelled.message', {
                    hideLabelKey: true
                }),
                refused: getLabel('checkout.refused.message', {
                    hideLabelKey: true
                }),
                pending: getLabel('checkout.pending.message', {
                    hideLabelKey: true
                }),
                received: getLabel('checkout.received.message', {
                    hideLabelKey: true
                })
            },
            completed: getLabel('checkout.completed.message'),
            additionalMessage: getLabel('checkout.completed.additionalMessage', {
                hideLabelKey: true
            }),
            cartContainsContactLenses: getLabel('checkout.completed.cartContainsContactLenses'),
            cartContainsPrescriptionGlasses: getLabel('checkout.completed.cartContainsPrescriptionGlasses'),
            cartContainsSolutions: getLabel('checkout.completed.cartContainsSolutions'),
            cartContainsSunglasses: getLabel('checkout.completed.cartContainsSunglasses'),
            cartContainsSubscription: getLabel('checkout.completed.cartContainsSubscription'),
            paymentDetailsTitle: getLabel('checkout.completed.paymentDetails.title'),
            contactDetailsTitle: getLabel('checkout.completed.contactDetails.title'),
            shipmentDetailsTitle: getLabel('checkout.completed.shipmentDetails.title'),
            shippingAddress: getLabel('checkout.shipment.address'),
            billingAddress: getLabel('checkout.billingAddress'),
            invoiceLabel: getLabel('checkout.invoice.label'),
            company: getLabel('checkout.orderType.company'),
            serviceStore: getLabel('checkout.serviceStore.title'),
            backToStore: getLabel('checkout.actions.backToStore'),
            yourDetails: getLabel('checkout.yourDetails'),
            summary: {
                delivery: getLabel('checkout.summary.shipment.delivery'),
                deliveryExpress: getLabel('checkout.summary.shipment.deliveryExpress'),
                pickupStore: getLabel('checkout.summary.shipment.pickup'),
                pickupPoint: getLabel('checkout.summary.shipment.pickupPoint'),
                nearestPickup: getLabel('checkout.summary.shipment.nearestPickup')
            },
            nearestPickupMessage: getLabel('checkout.summary.shipment.nearestPickup.message')
        })
    , [
        getLabel
    ]);
    const currentBreakpoint = MediaQuery.useBreakpoint();
    const isMobile = currentBreakpoint === MediaQuery.BreakpointName.PHONE || currentBreakpoint === MediaQuery.BreakpointName.TABLET_PORTRAIT;
    const cartContext = React.useContext(Context.CartContext);
    const callBackResultCode = PaymentTypes.CallbackResultCode[status];
    const cartContainsContactLenses = cartContext === null || cartContext === void 0 ? void 0 : (ref = cartContext.cart) === null || ref === void 0 ? void 0 : ref.contains.contactLenses;
    const cartContainsPrescriptionGlasses = cartContext === null || cartContext === void 0 ? void 0 : (ref1 = cartContext.cart) === null || ref1 === void 0 ? void 0 : ref1.contains.prescriptionGlasses;
    const cartContainsSolutions = cartContext === null || cartContext === void 0 ? void 0 : (ref2 = cartContext.cart) === null || ref2 === void 0 ? void 0 : ref2.contains.solutions;
    const cartContainsSunglasses = cartContext === null || cartContext === void 0 ? void 0 : (ref3 = cartContext.cart) === null || ref3 === void 0 ? void 0 : ref3.contains.sunglasses;
    const cartContainsSubscription = cartContext === null || cartContext === void 0 ? void 0 : (ref4 = cartContext.cart) === null || ref4 === void 0 ? void 0 : ref4.contains.subscription;
    const invoice = useSelector(getInvoice);
    const order = orderResponse;
    const selectedPaymentDetails = React.useMemo(()=>{
        if (!(order === null || order === void 0 ? void 0 : order.paymentInfo)) {
            return undefined;
        }
        return getSelectedPaymentDetails(order === null || order === void 0 ? void 0 : order.paymentInfo);
    }, [
        order === null || order === void 0 ? void 0 : order.paymentInfo
    ]);
    const selectedStore = useSelector(getSelectedStore);
    const serviceStore = useSelector(getServiceStore);
    const selectedPickupPoint = useSelector(getSelectedPickupPoint);
    const isCheckoutFailed = useSelector(isFailed);
    const summaries = useSummariesSlot(false);
    React.useEffect(()=>{
        if (order) {
            pushPurchaseEventToDataLayer(order, currency, cartId);
            trackOrderCompletionEvents(order, isMonetateProductDataEnabled);
        }
    }, [
        cartId,
        currency,
        order,
        isMonetateProductDataEnabled
    ]);
    const renderSupportFooter = ()=>{
        const supportPhoneNumber = getLabel('support.phoneNumber', {
            hideLabelKey: true
        });
        const supportEmail = getLabel('support.emailAddress', {
            hideLabelKey: true
        });
        return /*#__PURE__*/ React.createElement(SupportFooter, {
            message: getLabel('support.title'),
            phoneNumber: supportPhoneNumber || undefined,
            email: supportEmail || undefined
        });
    };
    if (!order && isLoading) {
        return /*#__PURE__*/ React.createElement(Loader, null);
    }
    return /*#__PURE__*/ React.createElement(SplitPane, {
        main: /*#__PURE__*/ React.createElement("div", {
            className: "checkout-section checkout-section--main"
        }, /*#__PURE__*/ React.createElement("div", {
            className: "checkout-section__header"
        }, logo && /*#__PURE__*/ React.createElement(Logo, {
            url: "/",
            image: logo
        })), isMobile && /*#__PURE__*/ React.createElement("div", {
            className: "checkout-section__cart"
        }, cart ? cart : null), /*#__PURE__*/ React.createElement("div", {
            className: "checkout-section__body"
        }, isCheckoutFailed && /*#__PURE__*/ React.createElement(Alert, {
            className: "checkout-section__alert",
            type: "error",
            title: getLabel('checkout.errors.general.title'),
            message: getLabel('checkout.errors.general.message')
        }), /*#__PURE__*/ React.createElement(OrderConfirm, {
            callbackResultCode: callBackResultCode,
            cartContainsContactLenses: cartContainsContactLenses,
            cartContainsPrescriptionGlasses: cartContainsPrescriptionGlasses,
            cartContainsSolutions: cartContainsSolutions,
            cartContainsSubscription: cartContainsSubscription,
            cartContainsSunglasses: cartContainsSunglasses,
            invoice: invoice,
            labels: labels,
            order: order,
            selectedPaymentDetails: selectedPaymentDetails,
            selectedPickupPoint: selectedPickupPoint,
            selectedStore: selectedStore,
            serviceStore: serviceStore,
            shippingMethodsAsides: shippingMethodsAsides,
            summaries: summaries
        }))),
        aside: /*#__PURE__*/ React.createElement("div", {
            className: "checkout-section checkout-section--aside"
        }, !isMobile && /*#__PURE__*/ React.createElement("div", {
            className: "checkout-section__cart"
        }, cart ? cart : null), /*#__PURE__*/ React.createElement("div", {
            className: "checkout-section__support-footer"
        }, renderSupportFooter()))
    });
};

export { OrderConfirmContainer };
//# sourceMappingURL=OrderConfirmContainer.js.map
